import { useState, type FC } from 'react';
import { useFormContext } from 'react-hook-form';

import { useTranslation } from '@mint-lib/i18n';
import { ArrowLeft, CheckmarkOutline, Delete, View } from '@mint-lib/icons';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import {
  Button,
  Chip,
  CircularLoadingButton,
  Divider,
  typographyVariants,
} from '@mint-lib/tw-components';
import { StaticDateSpan } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';
import { Link } from '@mint/routing';
import { parseISO } from 'date-fns';

import ConfirmChangeInNewProcessModal from '../../components/modals/ConfirmChangeInNewProcessModal.jsx';
import DeleteFlowModal from '../../components/modals/DeleteFlowModal.jsx';
import PreviewFlowModal from '../../components/modals/PreviewFlowModal.jsx';
import {
  ApprovalSequenceType,
  type FlowFormInput,
} from '../../types/singleFlow.js';
import { convertToApprovalSequence } from '../../utils/singleFlow.js';

type Props = {
  flowName: string;
  lastEditedAt: string;
  isFlowInUse: boolean;
  isPending: boolean;
  onDelete: () => void;
};

const EditApprovalHeader: FC<Props> = ({
  flowName,
  lastEditedAt,
  isFlowInUse,
  isPending,
  onDelete,
}) => {
  const { t } = useTranslation('@myn/approval_v2');
  const isReadOnly = !useIsAllowedPermission('approvalFlowFeature:modify');
  const [approvalSequence, setApprovalSequence] =
    useState<ApprovalSequenceType | null>(null);
  const [
    isFlowChangeConfirmationModalOpen,
    setIsFlowChangeConfirmationModalOpen,
  ] = useState(false);

  const {
    formState: { isDirty, isValid },
    getValues,
  } = useFormContext<FlowFormInput>();

  const handleOpenPreviewModal = () => {
    const { approvers } = getValues();
    const sequence = convertToApprovalSequence(approvers);
    setApprovalSequence(sequence);
  };

  const handleClosePreviewModal = () => {
    setApprovalSequence(null);
  };

  const handleOpenChangeConfirmationModal = () => {
    setIsFlowChangeConfirmationModalOpen(true);
  };

  const handleCloseChangeConfirmationModal = () => {
    setIsFlowChangeConfirmationModalOpen(false);
  };

  return (
    <>
      <div>
        <div className="flex justify-between gap-5">
          <div>
            <Link
              to="/company-profile/flows"
              className={cn(
                typographyVariants({
                  variant: 'bodyShort02',
                  color: 'link',
                }),
                'item-center flex gap-1',
              )}
              data-dd-action-name="Navigate to the Approval Hub page"
            >
              <ArrowLeft fill="currentColor" />
              {t('Back to approval flows page')}
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={typographyVariants({
                variant: 'caption1',
                color: 'secondary',
              })}
            >
              {t('Last edit on')}:
            </span>
            <Chip variant="outline" size="medium" color="silver">
              <StaticDateSpan date={parseISO(lastEditedAt)} />
            </Chip>
            <Divider orientation="vertical" className="h-6!" />
            <Button
              data-dd-action-name="Delete the flow"
              variant="outlined"
              color="error"
              size="medium"
              type="button"
              onClick={onDelete}
              disabled={isReadOnly}
            >
              <Delete />
              {t('Delete flow')}
            </Button>
            <Button
              data-dd-action-name="Show preview modal"
              variant="outlined"
              color="primary"
              size="medium"
              type="button"
              onClick={handleOpenPreviewModal}
              disabled={isReadOnly || !isValid}
            >
              <View />
              {t('Preview')}
            </Button>
            <Button
              data-dd-action-name="Save changes of the flow"
              className="min-w-24"
              variant="contained"
              color="primary"
              size="medium"
              type={isFlowInUse ? 'button' : 'submit'}
              onClick={
                isFlowInUse ? handleOpenChangeConfirmationModal : undefined
              }
              disabled={isReadOnly || !isDirty || !isValid || isPending}
            >
              {isPending ? (
                <CircularLoadingButton size="small" variant="contained" />
              ) : (
                <CheckmarkOutline />
              )}
              {t('Update')}
            </Button>
          </div>
        </div>
        <h2 className={cn(typographyVariants({ variant: 'h2' }))}>
          {flowName}
        </h2>
      </div>
      <PreviewFlowModal
        approvalSequence={approvalSequence}
        onClose={handleClosePreviewModal}
      />
      <DeleteFlowModal />
      <ConfirmChangeInNewProcessModal
        open={isFlowChangeConfirmationModalOpen}
        onClose={handleCloseChangeConfirmationModal}
      />
    </>
  );
};

export default EditApprovalHeader;
